import DownloadMinimalistic from '@betfan/icons/linear/ArrowsAction/DownloadMinimalistic.svg?react';
import CloseIcon from 'assets/images/closeIconGray.svg?react';
import installPwaImg from 'assets/images/install-pwa-confirm.png';
import cn from 'classnames';
import { DSButton } from 'designSystem';
import { getSelectedCampaignOption } from 'features/AICampaign/AICampaignIcon';
import { AppStoreButton } from 'features/appStoreButton/appStoreButton';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addNotification } from 'shared/components/toast';
import { pwaInstallConfig } from 'shared/constants/common';
import { getDeviceType } from 'shared/hooks/useDeviceManager';
import usePwaPrompt from 'shared/hooks/usePwaPrompt';
import { sendAppInstallCloseEvent, sendAppInstallStartEvent, sendAppInstallSuccessEvent } from '../analytics';

interface Props {
	forceUpdate: any;
}

const BaseMobileHomepage: React.FC<Props> = (props) => {
	const { t } = useTranslation();
	const device = getDeviceType();
	const pwaPrompt = usePwaPrompt();
	const [show, setShow] = useState(false);
	const [confirm, setConfirm] = useState(false);
	const [hidden, setHidden] = useState(false);
	const timestamp: null | string = localStorage.getItem(pwaInstallConfig.STORAGE_TIMESTAMP_KEY);
	const selectedCampaignOption = getSelectedCampaignOption();
	const isIos = device?.deviceType === 'ios';

	const aICampaignImages = (
		<>
			<div className="bg-[url(assets/images/pudzianAI/pudzianAIBanner.png)] bg-no-repeat bg-contain absolute bottom-0 left-2 [@media(min-width:600px)]:left-[230px]    w-[325px] h-[180px] z-[1] " />
			<div className="bg-[url(assets/images/pudzianAI/pudzianAIBannerText.svg)] bg-no-repeat bg-contain absolute min-w-[197px] w-[50%] [@media(min-width:600px)]:w-[280px] h-full z-[1] right-4 top-4" />
		</>
	);

	const standardImages = (
		<>
			<div className="bg-[url(assets/images/install-pwa.jpg)] bg-no-repeat bg-contain absolute bottom-0 left-[40%] -ml-[230px] [@media(min-width:600px)]:left-[230px]    w-[325px] h-[180px] z-[1] " />
			<div className="bg-[url(assets/images/install-pwa-text.svg)] bg-no-repeat bg-contain absolute min-w-[210px] w-[50%] [@media(min-width:600px)]:w-[280px] h-full z-[1] right-4 top-4" />
		</>
	);

	const shouldShowNativeAppBanner = () => {
		if (device) {
			const android = pwaPrompt.canInstall && device.deviceType === 'android' && device?.clientType === 'browser';
			const ios = device.deviceType === 'ios' && device.clientType !== 'native';
			return android || ios;
		} else {
			return false;
		}
	};

	const initialize = () => {
		if (shouldShowNativeAppBanner()) {
			if (timestamp) {
				setShow(Date.now() > parseInt(timestamp));
			} else {
				setShow(true);
			}
		} else {
			setShow(false);
		}
	};

	const dismiss = () => {
		localStorage.setItem(
			pwaInstallConfig.STORAGE_TIMESTAMP_KEY,
			(Date.now() + parseInt(pwaInstallConfig.REMINDER_AGAIN_IN_MINUTES) * 60 * 1000).toString(),
		);
		setConfirm(true);
		sendAppInstallCloseEvent();
	};

	const install = () => {
		sendAppInstallStartEvent('homepage');
		pwaPrompt
			.install()
			.then((status) => {
				if (status === 'accepted') {
					pwaPrompt.reset();
					sendAppInstallSuccessEvent('homepage');
					setShow(false);
				} else {
					dismiss();
				}
			})
			.catch(() => {
				addNotification('error', t('installPwaError'));
			});
	};

	const hide = () => {
		setHidden(true);
		setTimeout(() => {
			setShow(false);
		}, 400);
	};

	useEffect(() => {
		if (!confirm) {
			initialize();
		}
	}, [props.forceUpdate, pwaPrompt.canInstall]);

	return show ? (
		<div
			className={cn('perspective-[800px] transition-height duration-300 overflow-hidden relative h-[188px]', {
				'!h-0': hidden,
			})}
		>
			<div
				className={cn('preserve-3d relative rounded-4 h-[180px] transition-transform duration-300', {
					'rotate-x-5turn': confirm,
				})}
			>
				<div className="overflow-hidden backface-hidden absolute top-0 bottom-0 right-0 left-0 rounded-4 bg-primary-500 z-[1] flex justify-end items-end p-4">
					<CloseIcon onClick={dismiss} className="absolute z-[2] right-[15px] top-[15px]  text-white  w-[10px] h-[10px]" />

					{selectedCampaignOption ? aICampaignImages : standardImages}

					{!isIos ? (
						<div className="absolute z-[2] right-4 bottom-4">
							<DSButton color="primary" size="small" leftIcon={DownloadMinimalistic} onClick={install}>
								{t('installPwaButton')}
							</DSButton>
						</div>
					) : (
						<AppStoreButton location="homepage" />
					)}
				</div>

				<div className="backface-hidden rotate-x-5turn absolute top-0 bottom-0 right-0 left-0 rounded-4 bg-white dark:bg-dark-200 z-[2]">
					<div className="h-[180px] overflow-hidden">
						<div className="flex flex-col-reverse p-4 [@media(min-width:390px)]:p-0 [@media(min-width:390px)]:flex-row">
							<img
								alt={t('installOurApp')}
								src={installPwaImg}
								className="object-contain w-1/2 mt-2 [@media(min-width:390px)]:mt-0 [@media(min-width:390px)]:p-1 [@media(min-width:390px)]:w-1/3"
							/>
							<div className=" [@media(min-width:390px)]:m-4 !paragraph-sm !font-medium !text-primary-500 dark:!text-primary-100">
								{t('installPwaInfo')}
							</div>
						</div>
						<div className="absolute z-[2] right-5 bottom-5">
							<DSButton
								color="primary"
								size="medium"
								className="!bg-primary-100 dark:!bg-dark-400 dark:!text-gray-100 !h-[36px] !text-label-xs !px-4"
								onClick={() => hide()}
							>
								{t('installPwaClose')}
							</DSButton>
						</div>
					</div>
				</div>
			</div>
		</div>
	) : null;
};

export const MobileHomepage = memo(BaseMobileHomepage);
